import {
  changePassword,
  confirmTotp,
  Dialog,
  InvalidInput,
  startTotp,
  turnTotpOff,
  type Passkey,
  type TotpSetup,
} from '@trippath/shared';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { CodeField } from '../../components/security/CodeField';
import { problemKey } from '../../lib/format';

/** A field of a dialog's form. */
function Field({
  id,
  label,
  type,
  autoComplete,
  value,
  error,
  hint,
  inputRef,
  onChange,
}: {
  id: string;
  label: string;
  type: 'text' | 'password';
  autoComplete: string;
  value: string;
  error: string | undefined;
  hint?: string;
  /** The field a dialog starts on. */
  inputRef?: RefObject<HTMLInputElement | null>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        className="text-input"
        type={type}
        autoComplete={autoComplete}
        value={value}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {hint !== undefined && <p className="quiet">{hint}</p>}
      {error !== undefined && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

/** The words for what the server said of each field; `failed` for anything else. */
function heard(
  error: unknown,
  words: (field: string, problem: string) => string,
): Record<string, string> {
  if (!(error instanceof InvalidInput)) return { failed: '' };
  return Object.fromEntries(error.fields.map((f) => [f.field, words(f.field, f.problem)]));
}

function Actions({ onCancel, label }: { onCancel: () => void; label: string }) {
  const { t } = useTranslation();
  return (
    <div className="dialog-actions">
      <button type="button" className="button" onClick={onCancel}>
        {t('security.cancel')}
      </button>
      <button type="submit" className="button button-primary">
        {label}
      </button>
    </div>
  );
}

/** The password changed, asked with the one it has. */
export function PasswordDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const first = useRef<HTMLInputElement>(null);
  return (
    <Dialog open onClose={onClose} title={t('security.changePassword')} initialFocus={first}>
      <form
        className="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setErrors({});
          changePassword(current, password).then(onSaved, (error: unknown) => {
            setErrors(
              heard(error, (field, problem) =>
                field === 'current' ? t('security.wrongCurrent') : t(problemKey(problem)),
              ),
            );
          });
        }}
      >
        <Field
          id={`${ids}-current`}
          label={t('security.current')}
          type="password"
          autoComplete="current-password"
          inputRef={first}
          value={current}
          error={errors.current}
          onChange={setCurrent}
        />
        <Field
          id={`${ids}-new`}
          label={t('security.newPassword')}
          type="password"
          autoComplete="new-password"
          value={password}
          error={errors.password}
          hint={t('security.newHint')}
          onChange={setPassword}
        />
        {errors.failed !== undefined && (
          <p className="form-failed" role="alert">
            {t('security.failed')}
          </p>
        )}
        <Actions onCancel={onClose} label={t('security.save')} />
      </form>
    </Dialog>
  );
}

/** The backup codes, said once: to download or copy. */
export function CodeList({ codes }: { codes: string[] }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  return (
    <div className="backup-codes-block">
      <ul className="backup-codes" aria-label={t('security.keepCodes')}>
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="backup-codes-actions">
        <a
          className="button button-small"
          download="trippath-backup-codes.txt"
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(`TripPath\n\n${text}\n`)}`}
        >
          {t('security.download')}
        </a>
        <button
          type="button"
          className="button button-small"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
            });
          }}
        >
          {t(copied ? 'security.copied' : 'security.copy')}
        </button>
      </div>
    </div>
  );
}

/** New backup codes, shown once. */
export function CodesDialog({
  codes,
  title,
  onClose,
}: {
  codes: string[];
  title: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open onClose={onClose} title={title}>
      <div className="form">
        <p className="quiet">{t('security.codesLead')}</p>
        <CodeList codes={codes} />
        <div className="dialog-actions">
          <button type="button" className="button button-primary" onClick={onClose}>
            {t('security.done')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** A key in groups of four, as it is typed: `JBSW Y3DP EHPK`. */
const grouped = (secret: string) => secret.replace(/(.{4})(?=.)/g, '$1 ');

/**
 * Two-step sign-in turned on, or moved to a new phone: the app shown its QR code, its first
 * code typed, the backup codes kept.
 */
export function SetupDialog({ moving, onClose }: { moving: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const ids = useId();
  const [setup, setSetup] = useState<TotpSetup | 'failed' | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [codes, setCodes] = useState<string[] | null>(null);
  useEffect(() => {
    startTotp().then(setSetup, () => {
      setSetup('failed');
    });
  }, []);
  const title = t(moving ? 'security.phoneTitle' : 'security.setupTitle');
  return (
    <Dialog open onClose={onClose} title={title}>
      {setup === null && <p className="quiet dialog-body">{t('trips.loading')}</p>}
      {setup === 'failed' && (
        <p className="form-failed dialog-body" role="alert">
          {t('security.failed')}
        </p>
      )}
      {setup !== null && setup !== 'failed' && (
        <form
          className="form security-steps"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setError(undefined);
            confirmTotp(code).then(setCodes, (failure: unknown) => {
              setError(
                t(failure instanceof InvalidInput ? 'security.wrongCode' : 'security.failed'),
              );
            });
          }}
        >
          <div
            className={codes === null ? 'security-step' : 'security-step security-step-done'}
          >
            <b aria-hidden="true">1</b>
            <div className="security-step-body">
              <strong>{t('security.scan')}</strong>
              <img
                className="security-qr"
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.svg)}`}
                alt={t('security.scan')}
                width={160}
                height={160}
              />
              <span className="quiet">{t('security.manual')}</span>
              <span className="mono security-secret">{grouped(setup.secret)}</span>
            </div>
          </div>
          <div
            className={codes === null ? 'security-step' : 'security-step security-step-done'}
          >
            <b aria-hidden="true">2</b>
            <div className="security-step-body">
              <strong>{t('security.enterCode')}</strong>
              {codes === null && (
                <>
                  <CodeField
                    id={`${ids}-code`}
                    label={t('security.code')}
                    value={code}
                    error={error}
                    onChange={setCode}
                  />
                  <button type="submit" className="button button-primary">
                    {t('security.confirm')}
                  </button>
                </>
              )}
            </div>
          </div>
          {codes !== null && (
            <div className="security-step">
              <b aria-hidden="true">3</b>
              <div className="security-step-body">
                <strong>{t('security.keepCodes')}</strong>
                <span className="quiet">{t('security.codesLead')}</span>
                <CodeList codes={codes} />
                <button type="button" className="button button-primary" onClick={onClose}>
                  {t('security.done')}
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </Dialog>
  );
}

/** Two-step sign-in turned off, asked with the password and a code. */
export function OffDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const ids = useId();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const first = useRef<HTMLInputElement>(null);
  return (
    <Dialog open onClose={onClose} title={t('security.offTitle')} initialFocus={first}>
      <form
        className="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setErrors({});
          turnTotpOff(password, code).then(onDone, (error: unknown) => {
            setErrors(
              heard(error, (field) =>
                t(field === 'password' ? 'security.wrongPassword' : 'security.wrongCode'),
              ),
            );
          });
        }}
      >
        <Field
          id={`${ids}-password`}
          label={t('security.password')}
          type="password"
          autoComplete="current-password"
          inputRef={first}
          value={password}
          error={errors.password}
          onChange={setPassword}
        />
        <Field
          id={`${ids}-code`}
          label={t('security.codeOrBackup')}
          type="text"
          autoComplete="one-time-code"
          value={code}
          error={errors.code}
          onChange={setCode}
        />
        {errors.failed !== undefined && (
          <p className="form-failed" role="alert">
            {t('security.failed')}
          </p>
        )}
        <Actions onCancel={onClose} label={t('security.turnOff')} />
      </form>
    </Dialog>
  );
}

/** A passkey renamed. */
export function NameDialog({
  passkey,
  onClose,
  onSave,
}: {
  passkey: Passkey;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const [name, setName] = useState(passkey.name);
  const [error, setError] = useState<string | undefined>(undefined);
  const first = useRef<HTMLInputElement>(null);
  return (
    <Dialog open onClose={onClose} title={t('security.renameTitle')} initialFocus={first}>
      <form
        className="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === '') {
            setError(t('problem.required'));
          } else {
            onSave(name.trim());
          }
        }}
      >
        <Field
          id={`${ids}-name`}
          label={t('security.passkeyName')}
          type="text"
          autoComplete="off"
          inputRef={first}
          value={name}
          error={error}
          onChange={setName}
        />
        <Actions onCancel={onClose} label={t('security.save')} />
      </form>
    </Dialog>
  );
}
